import { useState } from 'react'

import { useApolloClient } from '@apollo/client'
import { Box, Button, Icon, ProgressBar, Text } from '@factorialco/deprecated-design-system'
import {
  DataTableFilter,
  FilterType,
  ProjectionKind,
  useReconciliationTransactionDataTable,
} from 'generated/resources/datatable.graphql'
import { BankingTransactionFilter } from 'generated/resources/graphql'
import { sum } from 'lodash'

import DataTable from 'modules/core/components/DataTable'
import PopoverMenu from 'modules/core/components/PopoverMenu'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useEffect } from 'modules/core/hooks/useEffect'
import { dateBackendDateTimeToUI } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import { formattedAmount } from 'modules/banking/lib/paymentHelpers'

interface Props {
  onSelect: (transactionId: number | null) => void
  onSnooze: (transactionId: number, snoozeTime: Date) => void
  onUnsnooze: (transactionId: number) => void
  selectedTransactionId?: number | null
  bankAccountId: string
}

const EmptyBlock = () => (
  <Box
    height="full"
    flexDirection="column"
    gap="s8"
    width="s400"
    paddingX="s24"
    alignItems="center"
    textAlign="center"
  >
    <Icon.Large icon="file" color="grey600" />
    <Text.Header2>{i18n.t('banking.reconciliations.transactions_empty_state.title')}</Text.Header2>
    <Text.Normal color="grey800">
      {i18n.t('banking.reconciliations.transactions_empty_state.description')}
    </Text.Normal>
  </Box>
)

export interface TransactionDataTableFields {
  id: number
  bookingDate: string
  fullyReconciledAt?: string | null
  description?: string | null
  amountCents: number
  currency: string
  reconciliations: {
    __typename?: 'BankingReconciliation'
    id: number
    transactionReconciledAmountCents: number
    reconcilableReconciledAmountCents?: number | null
    invoice?: {
      __typename?: 'FinanceInvoice'
      id: number
      documentNumber?: string | null
      currency?: string | null
      documentDate?: string | null
      vendor?: {
        __typename?: 'FinanceVendor'
        id: number
        legalName: string
      } | null
    } | null
  }[]
  reconciliationSuggestions: {
    __typename?: 'BankingReconciliationSuggestion'
    id: number
    rejected?: boolean
    invoice?: {
      __typename?: 'FinanceInvoice'
      id: number
      documentNumber?: string | null
      totalAmountCents?: number | null
      dueDate?: string | null
      documentDate?: string | null
      currency?: string | null
      taxes: {
        __typename?: 'FinanceInvoiceTax'
        amountCents?: number | null
        baseAmountCents?: number | null
        percentage?: number | null
      }[]
      vendor?: {
        __typename?: 'FinanceVendor'
        id: number
        legalName: string
      } | null
    } | null
  }[]
}

const DAY_IN_MS = 24 * 3600 * 1000
const WEEK_IN_MS = 7 * DAY_IN_MS
const MONTH_IN_MS = 31 * DAY_IN_MS
const FOREVER_IN_MS = 200 * 12 * MONTH_IN_MS // 200 years

export const ReconciliationsTransactionsDataTable = ({
  onSelect,
  onSnooze,
  onUnsnooze,
  bankAccountId,
  selectedTransactionId,
}: Props) => {
  const {
    access: { locale },
  } = useCurrentSession()
  const [filter, setFilter] = useState('')

  const onRowSelect = (transactionId: number) => {
    onSelect(transactionId)
  }
  const apolloClient = useApolloClient()

  useEffect(() => {
    if (!selectedTransactionId) {
      apolloClient.refetchQueries({
        include: [source.query],
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTransactionId])

  const snoozeFor = (transactionId: number, time: number) => () => {
    onSnooze(transactionId, new Date(Date.now() + time))
  }

  const transactionTypeFilter: DataTableFilter<BankingTransactionFilter>[] = [
    {
      identifier: 'transactionBookingDateFilter',
      title: i18n.t('banking.attributes.booking_date'),
      path: 'bookingDate',
      default: false,
      type: FilterType.Date,
      categories: [
        {
          kind: 'DateTimePicker',
          operation: 'between',
          value: undefined,
        },
      ],
    },
    {
      identifier: 'transactionReconciledStatusFilter',
      title: i18n.t('banking.attributes.type'),
      path: 'reconciledStatus',
      default: true,
      type: FilterType.String,
      categories: [
        {
          kind: 'MultipleChoice',
          value: ['pending'],
          hideSearch: true,
          operation: 'in',
          options: [
            {
              label: i18n.t('banking.reconciliations.pending'),
              value: 'pending',
            },
            {
              label: i18n.t('banking.reconciliations.reconciled'),
              value: 'reconciled',
            },
            {
              label: i18n.t('banking.reconciliations.snoozed'),
              value: 'snoozed',
            },
          ],
        },
      ],
    },
  ]
  const source = useReconciliationTransactionDataTable(
    [
      {
        identifier: 'bookingDate',
        title: i18n.t('banking.attributes.booking_date'),
        kind: ProjectionKind.Custom,
        template: '{{ bookingDate }}',
        width: 128,
        render: (transaction) => (
          <Box
            style={{
              cursor: 'pointer',
            }}
            onClick={() => onRowSelect(transaction.id)}
            width="full"
            height="full"
          >
            <Text.Normal
              color={isSelectedTransaction(transaction.id) ? 'viridian1000' : undefined}
              size="200"
            >
              {dateBackendDateTimeToUI(transaction.bookingDate)}
            </Text.Normal>
          </Box>
        ),
      },
      {
        identifier: 'description',
        title: i18n.t('banking.attributes.description'),
        kind: ProjectionKind.Custom,
        template: '{{ description }}',
        render: (transaction) => {
          return (
            <Box style={{ cursor: 'pointer' }} onClick={() => onRowSelect(transaction.id)}>
              <Text.Normal
                ellipsis
                size="200"
                color={isSelectedTransaction(transaction.id) ? 'viridian1000' : undefined}
              >
                {transaction.description}
              </Text.Normal>
            </Box>
          )
        },
      },
      {
        identifier: 'amount',
        width: 190,
        title: i18n.t('banking.attributes.amount'),
        kind: ProjectionKind.Custom,
        template: '{{ amountCents }} {{ currency }}',
        render: (transaction) => {
          const totalReconciledAmountCents = sum(
            transaction.reconciliations.map((r) => r.transactionReconciledAmountCents)
          )
          const colorBar = isSelectedTransaction(transaction.id) ? 'viridian200' : 'grey400'
          const colorProgressBar = 'viridian800'
          return (
            <Box
              flexDirection="row"
              alignItems="center"
              justifyContent="spaceBetween"
              style={{ cursor: 'pointer', maxWidth: '180px' }}
              onClick={() => onRowSelect(transaction.id)}
            >
              <Box style={{ width: '100px' }}>
                <Text.Normal
                  ellipsis
                  weight="medium"
                  size="200"
                  color={isSelectedTransaction(transaction.id) ? 'viridian1000' : 'grey1000'}
                >
                  {formattedAmount({
                    amountCents: transaction.amountCents,
                    currency: transaction.currency,
                    locale: locale,
                  })}
                </Text.Normal>
              </Box>
              <Box minWidth="s48">
                <Box width="full">
                  <ProgressBar
                    barColor={colorBar}
                    progressColor={colorProgressBar}
                    size="medium"
                    amount={Math.abs(totalReconciledAmountCents)}
                    total={Math.abs(transaction.amountCents)}
                  />
                </Box>
              </Box>
            </Box>
          )
        },
      },
      {
        identifier: 'snoozes',
        kind: ProjectionKind.Decorative,
        width: 32,
        render: (transaction) => {
          const isFullyReconciled = transaction.fullyReconciledAt !== null
          return (
            <Box
              style={{
                height: '20px',
                marginTop: '-10px',
                marginLeft: '-20px',
              }}
            >
              <PopoverMenu type="list">
                <PopoverMenu.Trigger asChild>
                  <Button iconName="ellipsis" type="secondary" />
                </PopoverMenu.Trigger>
                <PopoverMenu.Content side="bottom" align="end">
                  {transaction.snoozedUntil === null ? (
                    <>
                      <PopoverMenu.TouchableOption
                        title={i18n.t('banking.reconciliations.snooze_for_a_day')}
                        onClick={snoozeFor(transaction.id, DAY_IN_MS)}
                        disabled={isFullyReconciled}
                      />
                      <PopoverMenu.TouchableOption
                        title={i18n.t('banking.reconciliations.snooze_for_a_week')}
                        onClick={snoozeFor(transaction.id, WEEK_IN_MS)}
                        disabled={isFullyReconciled}
                      />
                      <PopoverMenu.TouchableOption
                        title={i18n.t('banking.reconciliations.snooze_for_a_month')}
                        onClick={snoozeFor(transaction.id, MONTH_IN_MS)}
                        disabled={isFullyReconciled}
                      />
                      <PopoverMenu.TouchableOption
                        title={i18n.t('banking.reconciliations.snooze_forever')}
                        onClick={snoozeFor(transaction.id, FOREVER_IN_MS)}
                        disabled={isFullyReconciled}
                      />
                    </>
                  ) : (
                    <PopoverMenu.TouchableOption
                      title={i18n.t('banking.reconciliations.unsnooze')}
                      onClick={() => onUnsnooze(transaction.id)}
                      disabled={isFullyReconciled}
                    />
                  )}
                </PopoverMenu.Content>
              </PopoverMenu>
            </Box>
          )
        },
      },
      {
        identifier: 'actions',
        kind: ProjectionKind.Decorative,
        width: 32,
        render: (transaction) => {
          return (
            <Box
              flexDirection="row"
              style={{
                cursor: 'pointer',
                width: '24px',
                height: '20px',
                marginTop: '-2px',
                marginLeft: '-20px',
              }}
              onClick={() => onRowSelect(transaction.id)}
            >
              <Icon.Medium
                icon="arrow-right"
                color={isSelectedTransaction(transaction.id) ? 'viridian1000' : undefined}
              />
            </Box>
          )
        },
      },
    ],
    {
      variables: {
        accountIds: Number(bankAccountId),
        status: 'done',
        description: filter,
      },
    }
  )

  const isSelectedTransaction = (id: number) => id === selectedTransactionId

  return (
    <Box minWidth="s600" overflowY="auto">
      <DataTable.Provider
        source={source}
        sort={{ path: 'bookingDate', direction: 'desc' }}
        filters={transactionTypeFilter}
        enableClearFilters
        pagination={{
          kind: 'offset',
        }}
      >
        <Box gap="s16">
          <DataTable.HeadBar
            disableDefaultActions
            search={{
              value: filter,
              onChange: (ev) => setFilter(ev.target.value),
              placeholder: i18n.t('banking.attributes.description'),
            }}
          />
          <DataTable.Table emptyState={<EmptyBlock />} />
        </Box>
      </DataTable.Provider>
    </Box>
  )
}
